import type { BaseMessage } from './types';

const RESPONSE_TYPE = '__channel_response';
const REQUEST_ID_KEY = '__requestId';
const DEFAULT_REQUEST_TIMEOUT = 5000;

export class ChannelMessaging {
	private port: MessagePort;
	private listeners: Map<string, Set<(payload: unknown, version: number) => void>>;
	private anyListeners: Set<(type: string, payload: unknown, version: number) => void>;
	private pendingRequests: Map<
		string,
		{ resolve: (value: unknown) => void; reject: (error: Error) => void }
	>;

	constructor(port: MessagePort) {
		this.port = port;
		this.listeners = new Map();
		this.anyListeners = new Set();
		this.pendingRequests = new Map();

		this.port.onmessage = (event: MessageEvent<BaseMessage>) => {
			const { type, payload, version } = event.data || {};
			if (!type) return;

			// Handle response messages for request/response pattern
			if (type === RESPONSE_TYPE && payload && typeof payload === 'object') {
				const { requestId, data } = payload as { requestId: string; data: unknown };
				const pending = this.pendingRequests.get(requestId);
				if (pending) {
					pending.resolve(data);
					this.pendingRequests.delete(requestId);
				}
				return;
			}

			// Notify specific listeners
			const callbacks = this.listeners.get(type);
			callbacks?.forEach((cb) => cb(payload, version));

			// Notify "any" listeners
			this.anyListeners.forEach((cb) => cb(type, payload, version));
		};
	}

	public send(type: string, payload: unknown, version: number = 1): void {
		this.port.postMessage({ type, payload, version });
	}

	public on(type: string, callback: (payload: unknown, version: number) => void): () => void {
		if (!this.listeners.has(type)) {
			this.listeners.set(type, new Set());
		}
		this.listeners.get(type)!.add(callback);

		return () => {
			this.listeners.get(type)?.delete(callback);
		};
	}

	public once(type: string, callback: (payload: unknown, version: number) => void): () => void {
		const wrappedCallback = (payload: unknown, version: number) => {
			unsubscribe();
			callback(payload, version);
		};
		const unsubscribe = this.on(type, wrappedCallback);
		return unsubscribe;
	}

	public onAny(callback: (type: string, payload: unknown, version: number) => void): () => void {
		this.anyListeners.add(callback);
		return () => {
			this.anyListeners.delete(callback);
		};
	}

	public request<T = unknown>(
		type: string,
		payload: unknown,
		timeoutMs: number = DEFAULT_REQUEST_TIMEOUT,
	): Promise<T> {
		return new Promise((resolve, reject) => {
			const requestId = crypto.randomUUID();

			const timer = setTimeout(() => {
				this.pendingRequests.delete(requestId);
				reject(new Error(`Request timeout: ${type}`));
			}, timeoutMs);

			this.pendingRequests.set(requestId, {
				resolve: (value) => {
					clearTimeout(timer);
					resolve(value as T);
				},
				reject: (error) => {
					clearTimeout(timer);
					reject(error);
				},
			});

			this.send(type, { ...(payload as object), [REQUEST_ID_KEY]: requestId });
		});
	}

	public reply(requestId: string, data: unknown): void {
		this.send(RESPONSE_TYPE, { requestId, data });
	}

	public disconnect(): void {
		// Reject all pending requests
		this.pendingRequests.forEach(({ reject }) => {
			reject(new Error('Channel disconnected'));
		});
		this.pendingRequests.clear();

		this.port.close();
		this.listeners.clear();
		this.anyListeners.clear();
	}
}
